import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ScoringRule } from '@/types/scoring';
import { buildScoringWeights } from '@/lib/trashScoring';
import { useTrashRankings, RankedPlayer, computeSeasonPoints } from './useTrashRankings';
import { PointsTimeFilter, getDateRange } from '@/lib/timeFilter';

// Column names in player_game_stats needed for scoring
const GAME_STAT_KEYS = [
  'player_id', 'is_pitcher',
  'strikeouts', 'errors', 'gidp', 'caught_stealing',
  'earned_runs', 'walks_allowed', 'wild_pitches', 'blown_saves',
  'hit_batsmen', 'home_runs_allowed', 'balks', 'passed_balls',
  'at_bats', 'hits', 'doubles', 'triples', 'home_runs',
  'rbi', 'runs_scored', 'stolen_bases', 'walks_drawn', 'hit_by_pitch',
  'pitcher_strikeouts', 'pitcher_wins', 'pitcher_losses', 'saves', 'holds',
  'outfield_assists', 'innings_pitched', 'hits_allowed', 'throwing_errors',
  'lob', 'complete_games', 'shutouts',
];

/**
 * Like useTrashRankings but supports time-window filtering.
 * 'season' delegates to the existing useTrashRankings (uses player_season_stats).
 * 7d/14d/30d aggregates from player_game_stats within the date window.
 */
export function useTimeFilteredRankings(
  scoringRules: ScoringRule[] | undefined,
  timeFilter: PointsTimeFilter,
  season: number = 2026,
): { rankings: RankedPlayer[]; loading: boolean; hasData: boolean } {
  // Season mode — delegate to existing hook
  const seasonResult = useTrashRankings(scoringRules, season, true);

  const [timeRanked, setTimeRanked] = useState<RankedPlayer[]>([]);
  const [timeLoading, setTimeLoading] = useState(false);
  const [timeHasData, setTimeHasData] = useState(false);

  const rulesKey = scoringRules
    ? JSON.stringify(scoringRules.map(r => `${r.id}:${r.pointValue}:${r.isEnabled}`))
    : '';

  const dateRange = useMemo(() => getDateRange(timeFilter), [timeFilter]);

  useEffect(() => {
    if (timeFilter === 'season' || !dateRange) return;

    let cancelled = false;
    const fetchData = async () => {
      setTimeLoading(true);

      const categoryConfig = scoringRules ? { scoring_rules: scoringRules } : null;
      const weights = buildScoringWeights(categoryConfig);

      // Fetch all active MLB players
      const pageSize = 1000;
      let allPlayers: any[] = [];
      let pFrom = 0;
      while (true) {
        const { data: pBatch, error: pErr } = await supabase
          .from('players')
          .select('id, full_name, position_abbreviation, position_eligibility, mlb_team_abbreviation, headshot_url, position_type')
          .eq('sport', 'mlb')
          .eq('status', 'Active')
          .range(pFrom, pFrom + pageSize - 1);
        if (pErr) break;
        allPlayers = allPlayers.concat(pBatch || []);
        if (!pBatch || pBatch.length < pageSize) break;
        pFrom += pageSize;
      }

      // Fetch game stats within date range
      let allGameStats: any[] = [];
      let gsFrom = 0;
      while (true) {
        const { data: gsBatch, error: gsErr } = await supabase
          .from('player_game_stats')
          .select(GAME_STAT_KEYS.join(', '))
          .gte('game_date', dateRange.start)
          .lte('game_date', dateRange.end)
          .range(gsFrom, gsFrom + pageSize - 1);
        if (gsErr) break;
        allGameStats = allGameStats.concat(gsBatch || []);
        if (!gsBatch || gsBatch.length < pageSize) break;
        gsFrom += pageSize;
      }

      if (cancelled) return;

      // Aggregate per player
      const agg = new Map<string, { stats: Record<string, number>; gamesPlayed: number; isPitcher: boolean }>();
      for (const gs of allGameStats) {
        let entry = agg.get(gs.player_id);
        if (!entry) {
          entry = { stats: {}, gamesPlayed: 0, isPitcher: false };
          agg.set(gs.player_id, entry);
        }
        entry.gamesPlayed++;
        if (gs.is_pitcher) entry.isPitcher = true;
        for (const key of GAME_STAT_KEYS) {
          if (key === 'player_id' || key === 'is_pitcher') continue;
          const val = Number(gs[key]) || 0;
          // Map 'lob' to 'left_on_base' for scoring compatibility
          const mappedKey = key === 'lob' ? 'left_on_base' : key;
          entry.stats[mappedKey] = (entry.stats[mappedKey] || 0) + val;
        }
      }

      setTimeHasData(allGameStats.length > 0);

      // Build player map for lookup
      const playerMap = new Map<string, any>();
      for (const p of allPlayers) playerMap.set(p.id, p);

      // Compute points and build ranked array
      const ranked: RankedPlayer[] = allPlayers.map(p => {
        const entry = agg.get(p.id);
        const { trash, anti, net } = entry
          ? computeSeasonPoints(entry.stats, weights)
          : { trash: 0, anti: 0, net: 0 };
        return {
          rank: 0,
          player_id: p.id,
          full_name: p.full_name,
          position_abbreviation: p.position_abbreviation,
          position_eligibility: p.position_eligibility || null,
          mlb_team_abbreviation: p.mlb_team_abbreviation,
          headshot_url: p.headshot_url,
          is_pitcher: entry?.isPitcher ?? p.position_type === 'Pitcher',
          games_played: entry?.gamesPlayed || 0,
          trash_points: trash,
          anti_points: anti,
          net_points: net,
          strikeouts: entry?.stats.strikeouts || 0,
          errors: entry?.stats.errors || 0,
          gidp: entry?.stats.gidp || 0,
          earned_runs: entry?.stats.earned_runs || 0,
          blown_saves: entry?.stats.blown_saves || 0,
          home_runs: entry?.stats.home_runs || 0,
          hits: entry?.stats.hits || 0,
          rbi: entry?.stats.rbi || 0,
          stolen_bases: entry?.stats.stolen_bases || 0,
          pitcher_strikeouts: entry?.stats.pitcher_strikeouts || 0,
          pitcher_wins: entry?.stats.pitcher_wins || 0,
          saves: entry?.stats.saves || 0,
        };
      });

      ranked.sort((a, b) => b.net_points - a.net_points);
      ranked.forEach((p, i) => { p.rank = i + 1; });

      if (!cancelled) {
        setTimeRanked(ranked);
        setTimeLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [timeFilter, dateRange?.start, dateRange?.end, rulesKey]);

  if (timeFilter === 'season') {
    return seasonResult;
  }

  return { rankings: timeRanked, loading: timeLoading, hasData: timeHasData };
}
